import React, { useState, useRef, useContext, useEffect } from 'react'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'

import './styles.scss'
import { useCommonStyles } from 'styles/globalStyles'

import ContractContext from '../../../../../store/contexts/contractContext'
import AUTH_ACTIONS from '../../../../../store/actions/authAction'

import Button from '../../../../../components/Button'
import ErrorComponent from 'components/ErrorComponent'
import UserExtraValues, { IUserData } from 'components/UserExtraValues'

import { newUpdateUser } from 'services/auth'

import { filterString } from 'helpers/cleanup'
import { formatUserData } from '../../../../../helpers/userData'

interface ILabelValue {
  label: keyof IUserData
  value: string
}

export default function CPFRegister() {
  const commonClasses = useCommonStyles()
  const {
    handleStepChange,
    setIsLoading,
    auth: { authDispatch, userDataForm: contextUserData },
  } = useContext(ContractContext)

  const localStorageUser = localStorage.getItem('current_user')
  let localStorageUserData
  if (localStorageUser) {
    const tempUserData = JSON.parse(localStorageUser)
    localStorageUserData = { accessToken: tempUserData.accessToken, ...tempUserData.user }
  }
  const currentUserData = !isEmpty(contextUserData) ? contextUserData : localStorageUserData

  const registerRef = useRef(null)
  const [errors, setErrors] = useState<string[]>([])
  const [userDataForm, setUserDataForm] = useState({
    name: { label: 'Nome', mandatory: true, value: '', error: '' },
    phone: { label: 'Telefone', mandatory: true, value: '', error: '' },
    cpf: { label: 'CPF', mandatory: true, value: '', error: '' },

    number: { label: 'Número', mandatory: true, value: '', error: '' },
    state: { label: 'Estado', mandatory: true, value: '', error: '' },
    zip_code: { label: 'CEP', mandatory: true, value: '', error: '' },
    street: { label: 'Nome da rua', mandatory: true, value: '', error: '' },
    city: { label: 'Cidade', mandatory: true, value: '', error: '' },
    complement: { label: 'Complemento', mandatory: false, value: '', error: '' },
    neighbourhood: { label: 'Bairro', mandatory: true, value: '', error: '' },
  })

  useEffect(() => {
    handleRegisteredData()
    // eslint-disable-next-line
  }, [])

  const handleRegisteredData = () => {
    if (currentUserData) {
      const { name, phone, cpf, address } = currentUserData

      const formData = {
        name: { ...userDataForm.name, value: name },
        phone: { ...userDataForm.phone, value: phone },
        cpf: { ...userDataForm.cpf, value: cpf },

        number: { ...userDataForm.number, value: get(address, 'number', '') },
        state: { ...userDataForm.state, value: get(address, 'state', '') },
        zip_code: { ...userDataForm.zip_code, value: get(address, 'zip_code', '') },
        street: { ...userDataForm.street, value: get(address, 'street', '') },
        city: { ...userDataForm.city, value: get(address, 'city', '') },
        complement: { ...userDataForm.complement, value: get(address, 'complement', '') },
        neighbourhood: { ...userDataForm.neighbourhood, value: get(address, 'neighbourhood', '') },
      }
      setUserDataForm(formData)
    }
  }

  const setUserDataFormError = (key: string, error: string) => {
    setUserDataForm((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        error,
      },
    }))
  }

  const handleUserData = ({ value, label }: ILabelValue) => {
    setUserDataForm((prev) => ({ ...prev, [label]: { ...prev[label], value } }))

    if (userDataForm[label].error) {
      setUserDataFormError(label, '')
    }
  }

  const scrollToErrors = () => {
    if (registerRef.current) {
      //@ts-ignore
      registerRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' })
    }
  }

  const validateUserDataForm = () => {
    let isValid = true
    Object.keys(userDataForm).forEach((field) => {
      if (userDataForm[field].mandatory && !userDataForm[field].value) {
        setUserDataFormError(field, `O campo ${userDataForm[field].label} é obrigatório`)
        isValid = false
      }
    })

    if (filterString(userDataForm.zip_code.value, ['_', '-']).length < 8) {
      setUserDataFormError('zip_code', 'Insira um CEP válido')
      isValid = false
    }

    return isValid
  }

  const handleUserDataUpdate = async () => {
    const isValid = validateUserDataForm()

    if (!isValid) {
      scrollToErrors()
      return
    }

    setIsLoading(true)
    const formattedUserData = formatUserData(userDataForm)

    authDispatch({
      type: AUTH_ACTIONS.SET_USER_EXTRA_DATA,
      value: formattedUserData,
    })

    try {
      const updatedUser = await newUpdateUser(formattedUserData, currentUserData.accessToken)
      localStorage.setItem(
        'current_user',
        JSON.stringify({ accessToken: currentUserData.accessToken, user: updatedUser }),
      )
      handleStepChange('TenantRegister')
    } catch (e) {
      console.log('update user error', { e })
      setErrors([
        'Não foi possível atualizar o cadastro. Por favor verifique os dados ou tente novamente mais tarde.',
      ])
    }

    setIsLoading(false)
  }

  return (
    <div ref={registerRef} className='data-register-container'>
      <p className={commonClasses.contractSubtitle}>
        A partir de agora, todos os dados informados serão usados para gerar o contrato de aluguel.
      </p>
      <UserExtraValues
        userType='owner'
        userData={userDataForm}
        handleData={handleUserData}
        subtitle={
          'Fique tranquilo com os seus dados. Nós nos dedicamos a proteger eles com todo o cuidado e segurança.'
        }
      />

      <ErrorComponent errors={errors} />

      <div className='end-aligner'>
        <div className='button-footer'>
          <Button title='Voltar' onClick={() => handleStepChange('CellPhoneValidation')} />
          <Button title='CADASTRAR' onClick={handleUserDataUpdate} />
        </div>
      </div>
    </div>
  )
}
